import { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Globe, Smartphone, Palette, Brain, Wrench, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const showcaseItems = [
  {
    id: 'website-development',
    title: 'Custom Websites',
    subtitle: 'Enterprise-Grade Web Solutions',
    description: 'From stunning landing pages to complex web applications. We build responsive, fast, and SEO-optimized websites that convert visitors into customers.',
    icon: Globe,
    color: '#3B82F6',
    features: ['Responsive Design', 'SEO Optimized', 'Fast Loading', 'Custom CMS'],
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800'
  },
  {
    id: 'app-development',
    title: 'Mobile & Web Apps',
    subtitle: 'Cross-Platform Development',
    description: 'Native and cross-platform mobile applications that deliver exceptional user experiences. iOS, Android, and Progressive Web Apps.',
    icon: Smartphone,
    color: '#8B5CF6',
    features: ['iOS & Android', 'React Native', 'Flutter', 'PWA Support'],
    image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800'
  },
  {
    id: 'uiux-design',
    title: 'UI/UX Design',
    subtitle: 'User-Centered Design',
    description: 'Beautiful, intuitive interfaces that users love. We combine aesthetics with functionality to create memorable digital experiences.',
    icon: Palette,
    color: '#F97316',
    features: ['User Research', 'Wireframing', 'Prototyping', 'Brand Identity'],
    image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=800'
  },
  {
    id: 'ai-solutions',
    title: 'Agentic AI',
    subtitle: 'Intelligent Automation',
    description: 'Harness the power of AI to automate workflows, gain insights, and create intelligent systems that learn and adapt.',
    icon: Brain,
    color: '#10B981',
    features: ['Custom AI Models', 'Chatbots', 'Automation', 'Data Analytics'],
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800'
  },
  {
    id: 'business-tools',
    title: 'Business Tools',
    subtitle: 'Streamline Operations',
    description: 'Custom software solutions to optimize your business processes. CRM, ERP, inventory management, and more.',
    icon: Wrench,
    color: '#EC4899',
    features: ['CRM Systems', 'ERP Solutions', 'Dashboards', 'Integrations'],
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800'
  }
];

const ServiceShowcase = () => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [direction, setDirection] = useState('next');
  const intervalRef = useRef(null);

  const currentItem = showcaseItems[currentIndex];
  const Icon = currentItem.icon;

  useEffect(() => {
    if (isAutoPlaying) {
      intervalRef.current = setInterval(() => {
        setDirection('next');
        setCurrentIndex((prev) => (prev + 1) % showcaseItems.length);
      }, 5000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAutoPlaying]);

  const goToNext = () => {
    setDirection('next');
    setCurrentIndex((prev) => (prev + 1) % showcaseItems.length);
    setIsAutoPlaying(false);
  };

  const goToPrev = () => {
    setDirection('prev');
    setCurrentIndex((prev) => (prev - 1 + showcaseItems.length) % showcaseItems.length);
    setIsAutoPlaying(false);
  };

  const goToSlide = (index) => {
    setDirection(index > currentIndex ? 'next' : 'prev');
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  return (
    <section className="relative py-24 md:py-32 overflow-hidden" style={{ background: 'var(--bg-dark)' }}>
      {/* Background Glow */}
      <div 
        className="absolute inset-0 transition-all duration-1000"
        style={{
          background: `radial-gradient(ellipse 60% 40% at 30% 50%, ${currentItem.color}15, transparent)`
        }}
      ></div>

      <div className="relative max-w-[1400px] mx-auto px-[7.6923%]">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--primary)]/10 border border-[var(--primary)]/30 rounded-full mb-6" style={{ borderColor: `${currentItem.color}30`, background: `${currentItem.color}10` }}>
            <span style={{ color: currentItem.color }} className="text-sm font-semibold">Featured Services</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-[var(--text-primary)] mb-6 leading-tight">
            What We{' '}
            <span className="bg-gradient-to-r from-[#3B82F6] via-[#8B5CF6] to-[#F97316] bg-clip-text text-transparent">
              Create
            </span>
          </h2>
        </div>

        {/* Showcase Card */}
        <div className="relative">
          <div 
            className="grid lg:grid-cols-2 gap-8 items-center bg-[var(--bg-card)] border border-[var(--border)] rounded-3xl p-8 md:p-12 transition-all duration-500"
            style={{ boxShadow: `0 25px 50px -12px ${currentItem.color}20` }}
          >
            {/* Content Side */}
            <div className={`space-y-6 transition-all duration-500 ${direction === 'next' ? 'animate-fade-in-left' : 'animate-fade-in-right'}`}>
              <div className="flex items-center gap-4">
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center transition-all duration-500"
                  style={{ background: `${currentItem.color}20`, border: `1px solid ${currentItem.color}40` }}
                >
                  <Icon size={32} style={{ color: currentItem.color }} />
                </div>
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)]">{currentItem.title}</h3>
                  <p style={{ color: currentItem.color }} className="text-sm font-medium">{currentItem.subtitle}</p>
                </div>
              </div>

              <p className="text-[var(--text-secondary)] text-lg leading-relaxed">
                {currentItem.description}
              </p>

              {/* Features */}
              <div className="grid grid-cols-2 gap-3">
                {currentItem.features.map((feature, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center gap-2 text-[var(--text-muted)] text-sm"
                    style={{ animationDelay: `${idx * 0.1}s` }}
                  >
                    <div 
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: currentItem.color }}
                    ></div>
                    {feature}
                  </div>
                ))}
              </div>

              {/* CTA */}
              <button
                onClick={() => navigate(`/services/${currentItem.id}`)}
                className="inline-flex items-center gap-3 px-6 py-3 text-white font-semibold rounded-xl transition-all duration-300 hover:scale-105 hover:gap-4"
                style={{ background: `linear-gradient(135deg, ${currentItem.color}, ${currentItem.color}CC)` }}
              >
                View Service
                <ArrowRight size={18} />
              </button>
            </div>

            {/* Image Side */}
            <div className={`relative transition-all duration-500 ${direction === 'next' ? 'animate-fade-in-right' : 'animate-fade-in-left'}`}>
              <div className="relative overflow-hidden rounded-2xl aspect-[4/3]">
                <img
                  src={currentItem.image}
                  alt={currentItem.title}
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                />
                <div 
                  className="absolute inset-0"
                  style={{ background: `linear-gradient(135deg, ${currentItem.color}40, transparent)` }}
                ></div>
              </div>

              {/* Floating Badge */}
              <div 
                className="absolute -bottom-4 -right-4 px-4 py-2 rounded-xl text-white text-sm font-semibold shadow-xl"
                style={{ background: currentItem.color }}
              >
                Starting at $999
              </div>
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={goToPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-6 w-12 h-12 bg-[var(--bg-card)] border border-[var(--border)] rounded-full flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[var(--border-hover)] transition-all shadow-lg hover:scale-110"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-6 w-12 h-12 bg-[var(--bg-card)] border border-[var(--border)] rounded-full flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[var(--border-hover)] transition-all shadow-lg hover:scale-110"
          >
            <ChevronRight size={24} />
          </button>
        </div>

        {/* Dots Navigation */}
        <div className="flex items-center justify-center gap-3 mt-8">
          {showcaseItems.map((item, index) => (
            <button
              key={item.id}
              onClick={() => goToSlide(index)}
              className={`transition-all duration-300 rounded-full ${
                index === currentIndex 
                  ? 'w-8 h-2' 
                  : 'w-2 h-2 bg-[var(--text-muted)]/30 hover:bg-[var(--text-muted)]/50'
              }`}
              style={index === currentIndex ? { background: item.color } : {}}
            />
          ))}
        </div>

        {/* Auto-play indicator */}
        <div className="text-center mt-4">
          <button
            onClick={() => setIsAutoPlaying(!isAutoPlaying)}
            className="text-[var(--text-muted)] text-xs hover:text-[var(--text-secondary)] transition-colors"
          >
            {isAutoPlaying ? '⏸ Auto-playing' : '▶ Click to auto-play'}
          </button>
        </div>
      </div>
    </section>
  );
};

export default ServiceShowcase;
